import { useState, useCallback, useEffect } from 'react'
import { prodexer, txHashToHex, fetchTransactionByHash } from '../api'
import type { Transaction, Transfer, Block } from '../types'
import {
  useQuery,
  SearchInput,
  Spinner,
  ErrorBanner,
  SectionHeader,
  AmountDisplay,
  formatNumber,
  truncate,
  formatTime,
  renderJsonData,
  ClickToCopy,
  LinkToCopy,
  walletUrl,
  blockUrl,
  txHashToDisplayHex,
} from '../components'

export function TransactionLookup({ initialQuery }: { initialQuery?: string }) {
  const txQ = useQuery<Transaction>()
  const transfersQ = useQuery<Transfer>()
  const blockQ = useQuery<Block>()

  const [hasSearched, setHasSearched] = useState(false)
  const [txHashInput, setTxHashInput] = useState('')

  const search = useCallback(async (txHashInputStr: string) => {
    setHasSearched(true)
    setTxHashInput(txHashInputStr)

    // Normalize the input: users may paste hex (0x...), raw hex, or base64.
    // txHashToHex handles all cases — strips 0x prefix, converts base64→\x hex.
    const txHash = txHashToHex(txHashInputStr.trim().replace(/^0[xX]/, ''))

    await Promise.all([
      txQ.execute(() => fetchTransactionByHash(txHash).then(tx => tx ? [tx] : [])),
      transfersQ.execute(() =>
        prodexer.query<Transfer>('transfers', {
          filters: [{ column: 'tx_hash', op: 'eq', value: txHash }],
          // No order: idx_transfers_tx_hash is single-column (tx_hash only).
          // order=block_index would fail canUseIndex. Sort client-side.
          limit: 100,
        })
      ),
    ])
  }, [txQ, transfersQ])

  // Auto-search on initialQuery (URL deep-link)
  useEffect(() => {
    if (initialQuery) {
      search(initialQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery])

  // After tx loads, fetch the block in a useEffect (NOT in render body)
  const tx = txQ.data[0]

  useEffect(() => {
    if (tx && !blockQ.loading && blockQ.data.length === 0) {
      blockQ.execute(() =>
        prodexer.query<Block>('blocks', {
          filters: [{ column: 'height', op: 'eq', value: String(tx.block_height) }],
          limit: 1,
        })
      )
    }
  }, [tx, blockQ])

  const loading = txQ.loading || transfersQ.loading
  const error = txQ.error || transfersQ.error
  const block = blockQ.data[0]

  return (
    <div>
      <SectionHeader
        title="Transaction Lookup"
        subtitle="Find a transaction by hash and see its details, transfers, and block context."
      />

      <SearchInput
        placeholder="Transaction hash (hex or 0x-prefixed)"
        onSubmit={search}
        loading={loading}
        buttonText="Lookup TX"
        currentValue={txHashInput}
      />

      {loading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {error && !loading && (
        <div className="mt-4">
          <ErrorBanner error={error} />
        </div>
      )}

      {!loading && hasSearched && !error && (
        <div className="mt-6 space-y-6">
          {tx ? (
            <>
              <div className="rounded-lg border border-border-default bg-surface p-4">
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Block Height</dt>
                    <dd className="text-sm font-mono text-accent-text">
                      <LinkToCopy text={String(tx.block_height)} href={blockUrl(tx.block_height)} display={<span>{formatNumber(tx.block_height)}</span>} />
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Block Index</dt>
                    <dd className="text-sm font-mono text-text-secondary">{tx.block_index}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">TX Hash</dt>
                    <dd className="text-sm font-mono text-text-secondary">
                      <ClickToCopy text={txHashToDisplayHex(tx.tx_hash)} display={<span>{truncate(txHashToDisplayHex(tx.tx_hash), 16, 8)}</span>} className="hover:text-accent-text" />
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Sender</dt>
                    <dd className="text-sm font-mono text-accent-text">
                      <LinkToCopy text={tx.sender_address} href={walletUrl(tx.sender_address)} display={<span>{truncate(tx.sender_address, 12, 8)}</span>} className="hover:text-accent-text" />
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Status</dt>
                    <dd className="text-sm font-mono">
                      <span className={tx.status_code === 0 ? 'text-success-text' : 'text-danger-text'}>
                        {tx.status_code === 0 ? '✓ Success' : `✗ Code ${tx.status_code}`}
                      </span>
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Gas Wanted</dt>
                    <dd className="text-sm font-mono text-text-secondary">{formatNumber(tx.gas_wanted)}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Gas Used</dt>
                    <dd className="text-sm font-mono text-text-secondary">
                      {formatNumber(tx.gas_used)}
                      {tx.gas_wanted > 0 && (
                        <span className="text-text-faint ml-1">
                          ({((tx.gas_used / tx.gas_wanted) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Codespace</dt>
                    <dd className="text-sm font-mono text-text-secondary">{tx.codespace ?? '—'}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Created At</dt>
                    <dd className="text-sm font-mono text-text-secondary">{formatTime(tx.created_at)}</dd>
                  </div>
                </dl>

                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint mb-1">Message Types</div>
                  <div className="flex flex-wrap gap-1.5">
                    {tx.message_types.map((mt, i) => (
                      <span
                        key={i}
                        className="rounded bg-bg-tertiary px-2 py-1 text-xs font-mono text-accent-text"
                      >
                        {mt}
                      </span>
                    ))}
                  </div>
                </div>

                {tx.memo && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wider text-text-faint mb-1">Memo</div>
                    <div className="text-sm font-mono text-text-muted">{tx.memo}</div>
                  </div>
                )}

                {block && (
                  <div className="mt-3 border-t border-border-default pt-3">
                    <div className="text-xs uppercase tracking-wider text-text-faint mb-1">Block Time</div>
                    <div className="text-sm font-mono text-text-secondary">{formatTime(block.consensus_time)}</div>
                  </div>
                )}

                {tx.raw_log && (
                  <div className="mt-3">
                    <details>
                      <summary className="cursor-pointer text-xs uppercase tracking-wider text-text-faint hover:text-text-muted">
                        Raw Log
                      </summary>
                      <pre className="mt-2 overflow-x-auto rounded bg-bg-primary p-3 text-xs font-mono text-text-muted max-h-48">
                        {tx.raw_log}
                      </pre>
                    </details>
                  </div>
                )}

                <div className="mt-3">
                  <details>
                    <summary className="cursor-pointer text-xs uppercase tracking-wider text-text-faint hover:text-text-muted">
                      JSON Data
                    </summary>
                    <pre className="mt-2 overflow-x-auto rounded bg-bg-primary p-3 text-xs font-mono text-text-muted max-h-64">
                      {renderJsonData(tx.json_data)}
                    </pre>
                  </details>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-text-secondary mb-2">
                  Transfers in this TX ({transfersQ.data.length})
                </h3>
                {transfersQ.data.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-border-default">
                    <table className="min-w-full divide-y divide-border-default">
                      <thead className="bg-bg-secondary">
                        <tr>
                          {['Sender', 'Recipient', 'Amount (ALLO)', 'Denom'].map((h) => (
                            <th key={h} className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-text-muted">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-faint">
                        {transfersQ.data.map((t, i) => (
                          <tr key={i} className="hover:bg-surface">
                            <td className="px-4 py-2 text-sm font-mono text-accent-text"><LinkToCopy text={t.sender} href={walletUrl(t.sender)} display={<span>{truncate(t.sender)}</span>} className="hover:text-accent-text" /></td>
                            <td className="px-4 py-2 text-sm font-mono text-success-text"><LinkToCopy text={t.recipient} href={walletUrl(t.recipient)} display={<span>{truncate(t.recipient)}</span>} className="hover:text-success-text" /></td>
                            <td className="px-4 py-2 text-sm font-mono text-amber-text"><AmountDisplay value={t.amount} className="text-amber-text" /></td>
                            <td className="px-4 py-2 text-sm font-mono text-text-muted">{t.denom}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-text-faint italic">No token transfers associated with this transaction.</div>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-text-faint italic">Transaction not found.</div>
          )}
        </div>
      )}

      {!hasSearched && (
        <div className="mt-8 text-center text-sm text-text-faint">
          Paste a transaction hash to see its details, transfers, and block context.
        </div>
      )}
    </div>
  )
}
